import { Box, Button, Checkbox, Chip, Divider, FormControlLabel, Typography } from '@mui/material';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { Controller, useController, useFormContext } from 'react-hook-form';
import { useMemo, useState } from 'react';
import DeliveryImage from '../../assets/images/3d-icons/delivery.png';
import SpotImage from '../../assets/images/3d-icons/spot.png';
import PackageImage from '../../assets/images/3d-icons/package.png';
import { useAdmnistrativeAreasQuery } from '../../queries/__generated__/administrativeAreas.generated';
import MultiChoicesAutocomplete from '../../common/components/MultiChoicesAutocomplete';
import MinimumOrderValueField from '../../common/components/MinimumOrderValueField';
import DeliveryTimesSelect from '../../company/delivery_zones/DeliveryTimesSelect';
import { DeliveryZonesForm } from './DeliveryZoneSettings';

interface DeliveryZoneSettingProps {
  index: number;
  deletable: boolean;
  deleteZone: () => void;
  standAlone?: boolean;
}

type AreaOption = 'all' | 'administrativeAreas';

export function DeliveryZoneSetting({ index, deletable, deleteZone, standAlone }: DeliveryZoneSettingProps): React.JSX.Element {
  const { t } = useTranslation(['settings', 'common', 'validation']);
  const { data } = useAdmnistrativeAreasQuery();
  const { control, watch } = useFormContext<DeliveryZonesForm>();
  const { field: areasField } = useController({
    name: `deliveryZones.${index}.areas`,
    control,
    rules: {
      validate: {
        required: (value) => !!value.length,
      },
    },
  });
  const [areaOption, setAreaOption] = useState<AreaOption>(areasField.value.includes('ALL_AREAS') ? 'all' : 'administrativeAreas');
  const deliveryZones = watch('deliveryZones');
  const isAllAreaSelected = deliveryZones.some(({ areas }) => areas[0] === 'ALL_AREAS');

  const administrativeAreaList = useMemo(() => {
    if (data?.administrative_area) {
      const selectedAreas = deliveryZones.reduce<string[]>((acc, current, currentIndex) => {
        if (currentIndex !== index) {
          acc.push(...current.areas);
        }

        return acc;
      }, []);

      return data.administrative_area.map(({ area }) => area).filter((area) => !selectedAreas.includes(area));
    }

    return [];
  }, [data, deliveryZones.map((zone) => zone.areas.join(''))]);

  const selectedAreas = useMemo(() => {
    return areasField.value.filter(
      (area: string) => area !== 'ALL_AREAS',
    );
  }, [areasField.value]);

  const handleAreaOptionChange = (option: AreaOption): void => {
    let areasToChange: string[] | undefined;

    if (option === 'all') {
      areasToChange = ['ALL_AREAS'];
    } else if (areasField.value.includes('ALL_AREAS')) {
      areasToChange = [];
    }

    if (areasToChange) {
      areasField.onChange(areasToChange);
    }
    setAreaOption(option);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 40 }}>
        <Typography variant="titleLarge">
          {t('settings:zone', { index: index + 1 })}
        </Typography>
        {!standAlone && (
          <Button
            variant="outlined"
            onClick={deleteZone}
            disabled={!deletable}
          >
            <DeleteOutlinedIcon />
          </Button>
        )}
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 0.5, pt: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <img src={PackageImage} height="24" width="24" />
          <Typography variant="titleMedium">
            {t('common:minimum_order_value')}
          </Typography>
        </Box>
        <MinimumOrderValueField
          minimumOrderValueName={`deliveryZones.${index}.mov`}
          minimumOrderValueTypeName={`deliveryZones.${index}.mov_type`}
        />
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            border: '1px solid',
            borderColor: 'outlineVariant.main',
            borderRadius: 2,
            pt: 0.5,
            pb: 1.5,
            px: 1,
          }}
        >
          <Controller
            control={control}
            name={`deliveryZones.${index}.negotiableMinimumOrderValue`}
            render={({ field }) => (
              <FormControlLabel
                control={<Checkbox {...field} checked={!!field.value} sx={{ ml: 0.5 }} />}
                label={(
                  <Typography variant="titleSmall">
                    {t('settings:negotiable_minimum_order_value')}
                  </Typography>
                )}
              />
            )}
          />
          <Typography variant="bodySmall" color="onSurfaceVariant">
            {t('settings:negotiable_minimum_order_value_description')}
          </Typography>
        </Box>
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 0.5, pt: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <img src={SpotImage} height="24" width="24" />
          <Typography variant="titleMedium">
            {t('settings:bound_areas')}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            <Chip
              className={areaOption === 'administrativeAreas' ? 'Kheops-selected' : ''}
              variant="filter"
              onClick={() => handleAreaOptionChange('administrativeAreas')}
              label={t('settings:by_area')}
            />
            <Chip
              className={areaOption === 'all' ? 'Kheops-selected' : ''}
              variant="filter"
              disabled={isAllAreaSelected && areasField.value[0] !== 'ALL_AREAS'}
              onClick={() => handleAreaOptionChange('all')}
              label={t(deliveryZones.length > 1 ? 'settings:rest_of_france' : 'settings:all_france')}
            />
          </Box>
        </Box>
        <MultiChoicesAutocomplete
          onChange={areasField.onChange}
          values={selectedAreas}
          disabled={areaOption === 'all'}
          sx={{
            width: '100%',
          }}
          InputProps={{
            startAdornment: (
              <SearchIcon fontSize="small" sx={{ mr: 1 }} />
            ),
          }}
          label={t('settings:define_area_in_this_zone')}
          options={administrativeAreaList}
        />
      </Box>
      <Divider />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 0.5, pt: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <img src={DeliveryImage} height="24" width="24" />
          <Typography variant="titleMedium">
            {t('settings:delivery_times')}
          </Typography>
        </Box>
        <Controller
          control={control}
          name={`deliveryZones.${index}.deliveryTime`}
          render={(({ field }) => (
            <DeliveryTimesSelect {...field} />
          ))}
        />
      </Box>
    </Box>
  );
}
